import t from '@rcm/i18n-client';
import { defineCustomElement } from '@rcm/react-utils';
import { useCustomElementValueChangeCallback } from 'forms';
import omit from 'lodash/omit';
import { createElement as e } from 'react';
import InvisibleReCaptcha2Button from '../components/InvisibleReCaptcha2Button.client';

/**
 * Registers a custom HTML element to display a button that triggers Invisible
 * ReCAPTCHA V2.
 *
 * The button itself is not invisible. Rather, "invisible" refers to to the
 * absence of any specific input field to contain a captcha challenge.
 *
 * > **NOTE:** This requires that the sitekey configured for ReCAPTCHA V2 is for
 * > invisible captcha.
 *
 * ## HTML Tag
 *
 * `<rcm-recaptcha-2-invisible-button>`
 *
 * ## HTML Attributes
 *
 * | Attribute | Description |
 * | --- | --- |
 * | `action` | Required. Designates the captcha action. |
 * | `btn` | Designates a Bootstrap `btn` class without the 'btn-' prefix. (For example, 'primary'.) |
 * | `disabled` | If passed, the button will be disabled. |
 * | `icon` | One of the Bootstrap 3 glyphicon names, without the prefix. If passed, the associated glyphicon will be shown left of the text. |
 * | `inner-class` | Will be passed as the 'class' to the inner button element. |
 * | `inner-id` | Will be passed as the ID of the inner button. |
 * | `inner-tabindex` | Will be passed as the `tabindex` of the inner button. |
 * | `irconr` | Same as `icon`, but icon shows right of the text. |
 * | `text`  | Text that will appear within the button. This will be automatically translated.  |
 * | `title` | Title that will popup when the mouse is over the button. This will be automatically translated. |
 * | `type` | Indicates the button action type; May be 'button', 'submit', or 'reset'. |
 *
 * ## DOM Events
 *
 * | Event | Description |
 * | --- | --- |
 * | `change` | This is fired when the captcha passes before the 'click' event when captcha is enabled in the current context. When captcha is disabled, this event fires as soon as the button is clicked, but the current value will be null. |
 *
 * ## Properties
 *
 * | Property | Description |
 * | --- | --- |
 * | `value` | Value of the captcha response. |
 *
 * ## Behavior When reCAPTCHA Is Disabled
 *
 * When captcha is disabled for the given action, behavior related to captcha
 * will not occur, but the 'change' event will still fire as soon as the button
 * is clicked, though the current value of the element will be set to null.
 *
 * See the `InvisibleReCaptcha2Button` component for details.
 *
 * ## Examples
 *
 * @example
 * ### HTML
 *
 * ```html
 * <rcm-recaptcha-2-invisible-button id="cap" action="my-action"></rcm-recaptcha-2-invisible-button>
 * ```
 *
 * ### JavaScript
 *
 * ```js
 * document.getElementById('cap').addEventListener('change', (e) => {
 *   const gCaptchaResponse = e.target.value;
 * });
 * ```
 */
const defineInvisibleReCaptcha2Button = () => {
  defineCustomElement({
    component: InvisibleReCaptcha2ButtonWrapper,
    tag: 'rcm-recaptcha-2-invisible-button',
  });
};

const InvisibleReCaptcha2ButtonWrapper = (attrs: Record<string, string>) => {
  const handleValueChange = useCustomElementValueChangeCallback();

  const {
    action,
    'inner-class': className,
    'inner-id': id,
    'inner-tabindex': tabIndex,
    ...props
  } = omit(attrs, ['class', 'id']);

  return e(InvisibleReCaptcha2Button, {
    ...props,
    action,
    className,
    id,
    text: props.text ? t(props.text) : null,
    title: props.title ? t(props.title) : null,
    ...(tabIndex ? { tabIndex: +tabIndex } : {}),
    onValueChange: handleValueChange,
  });
};

export default defineInvisibleReCaptcha2Button;
